import AccessTimeIcon from '@material-ui/icons/AccessTime';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import React from 'react';

import { AVERAGE_BLOCK_TIME_BSC } from '../../../constants/voting';
import { CountDown } from '../../common/countdown/CountDown.component';
import { Tooltip } from '../../core/tooltip';
import styles from './VotingCard.module.css';
import type { VotingFormProps } from './VotingForm.component';
import { VotingForm } from './VotingForm.component';

export interface VotingCardProps extends VotingFormProps {
  currentBlock: {
    timestamp: number;
    number: number;
  };
  numberOfVotes: string;
  getMoreStars: () => void;
}

export interface VoteProps extends VotingCardProps {
  hideModal?: () => void;
}

export const VotingCard: React.FC<VoteProps> = ({
  currentBlock,
  startBlock,
  endBlock,
  numberOfVotes,
  getMoreStars,
  hideModal = () => {},

  ...movieVotingFormProps
}) => {
  // 13.4s for average block time
  const endDifference = endBlock.number - currentBlock.number;
  const estimatedEndDate = new Date(
    Date.now() + endDifference * 1000 * AVERAGE_BLOCK_TIME_BSC,
  );
  const endDate = new Date(endBlock.timestamp * 1000);
  const difference = estimatedEndDate.getTime() - Date.now();
  const numDays = Math.floor(difference / (1000 * 60 * 60 * 24));

  const startDifference = startBlock.number - currentBlock.number;
  const estimatedStartDate = new Date(
    Date.now() + startDifference * 1000 * AVERAGE_BLOCK_TIME_BSC,
  );

  const timerText =
    startBlock.timestamp === 0 ? 'Voting starts' : 'Voting ended';
  const estimatedDate =
    startBlock.timestamp === 0 ? estimatedStartDate : endDate;

  return (
    <div className="w-full h-full relative z-10">
      <div
        className={`${styles.container} h-full relative flex flex-col items-center w-full bg-white bg-opacity-5 rounded-lg z-20`}
      >
        {endDifference > 0 && startBlock.timestamp > 0 ? (
          <div
            className={
              'flex flex-row items-center justify-center absolute rounded-full h-8 ' +
              styles.countDown
            }
          >
            <AccessTimeIcon className="ml-2" />
            <div className="ml-1 font-noto-sans font-bold">
              {numDays} day{numDays !== 1 && 's'}
            </div>
            <div className="ml-1 font-noto-sans opacity-20"> | </div>
            <div className="ml-1">
              <CountDown
                date={estimatedEndDate}
                numberSize="24px"
                days={false}
                secs={false}
                fontFamily="NotoSans"
                displayFirstLetter={true}
                fontSize={false}
                delimiter=":"
              />
            </div>
            <Tooltip
              title={`This is an estimate, the countdown will end at Ethereum block #${endBlock.number}.`}
              placement="top-end"
            >
              <HelpOutlineIcon className="ml-1 mr-2 opacity-50" />
            </Tooltip>
          </div>
        ) : (
          <div
            className={
              'flex flex-row items-center justify-center absolute rounded-full h-8 ' +
              styles.countDown
            }
          >
            <AccessTimeIcon className="ml-2" />
            <div className="ml-1 font-noto-sans font-bold mr-2">
              {timerText}
              {': '}
              {estimatedDate.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </div>
          </div>
        )}

        <div className="mt-10 text-5xl lg:text-6xl font-vanguard font-bold text-primary leading-10">
          {parseFloat(numberOfVotes).toLocaleString('en-us', {
            maximumFractionDigits: 0,
            useGrouping: true,
          })}{' '}
          votes
        </div>

        <VotingForm
          startBlock={startBlock}
          endBlock={endBlock}
          hideModal={hideModal}
          {...movieVotingFormProps}
        />
      </div>
    </div>
  );
};
